import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useAuth } from './auth-context.js';

/**
 * Google is the only provider, and the database rejects any address that is
 * not on the allowlist. A rejected account therefore comes back here with an
 * error in the URL rather than a blank page.
 */
export function LoginPage() {
  const { status, signInWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const redirectError = searchParams.get('error_description') ?? searchParams.get('error');
  const message = error ?? (redirectError ? describeAuthError(redirectError) : null);

  const handleSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Nem sikerült elindítani a bejelentkezést.',
      );
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <div className="login__card">
        <h1 className="login__title">Budget</h1>
        <p className="login__subtitle">Portfólió, kiadások és havi költések egy helyen.</p>

        {message ? (
          <p className="login__error" role="alert">
            {message}
          </p>
        ) : null}

        <button
          type="button"
          className="button button--primary login__button"
          onClick={() => void handleSignIn()}
          disabled={busy || status === 'loading'}
        >
          {busy ? 'Átirányítás…' : 'Belépés Google-fiókkal'}
        </button>

        <p className="login__note">Az app egyetlen engedélyezett fiókkal használható.</p>
      </div>
    </main>
  );
}

/** Turn Supabase's raw error text into something a human can act on. */
function describeAuthError(raw: string): string {
  const normalized = raw.toLowerCase();
  if (normalized.includes('not allowed') || normalized.includes('allowlist')) {
    return 'Ez a fiók nincs engedélyezve. Lépj be az engedélyezett Google-fiókkal.';
  }
  if (normalized.includes('signups not allowed') || normalized.includes('signup is disabled')) {
    return 'Új regisztráció ki van kapcsolva. Csak az engedélyezett fiók léphet be.';
  }
  return raw;
}
